import React, { useMemo, useState } from 'react';
import { BareContentTemplate, BareContentTemplateLegacy } from '@go-shared/types/contentTemplate';
import DraggableContentItem from '@go-client/components/DraggableContentItem';

import './ContentBlurbs.scss';
import {
  BubbleAlert,
  BubbleButton,
  BubbleFormGroup,
  BubbleInput,
  BubbleTextArea,
} from '@go-client/components/BubbleUI';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { FeedArea } from '@go-shared/types/feedTemplate';
import Fuse from 'fuse.js';
import { captureBetweenDollars } from '@go-shared/lib/utils';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { generateReplaceDict, generateTranslDict } from '@go-shared/lib/mergedVars';
import { ReplacedDict, TranslatedReplacedDict } from '@go-shared/types/mergedVars';
import BubbleTypeahead, {
  BubbleTypeaheadValue,
} from '@go-client/components/BubbleUI/BubbleTypeahead';
import { Property, PropertyTag } from '@go-shared/types/property';
import { intersection, mapBy, notUndefined } from '@go-shared/lib/functional';
import { convertContentTemplateToLegacy } from '@go-shared/lib/contentTemplate';
import { canManageLibraries } from '@go-shared/lib/permissions';
import { User } from '@go-shared/types/user';

interface ContentBlurbsProps {
  property: Property;
  user: User;
  variant: 'navy' | 'velvet';
  contentTemplates: BareContentTemplate[];
  onAddNewContentClick: () => void;
  onDoubleClick?: (content: BareContentTemplate) => void;
}

interface ContentSelectPageFilters {
  tag: Array<BubbleTypeaheadValue<PropertyTag>>;
}

export const ContentBlurbs: React.FC<ContentBlurbsProps> = ({
  property,
  user,
  variant,
  contentTemplates,
  onAddNewContentClick,
  onDoubleClick,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [typeaheadModes, setTypeaheadModes] = useState({
    tag: 'all',
  });
  const [filters, setFilters] = useState<ContentSelectPageFilters>({
    tag: [],
  });

  const tagsDict = mapBy(property.contentTags, '_id');

  const processedContents = useMemo(() => {
    return contentTemplates
      .sort((a, b) => {
        const aType = a.type || 'basic';
        const bType = b.type || 'basic';

        if (aType === bType) {
          return a.name.localeCompare(b.name);
        }

        //compare names alphabetically
        return aType.localeCompare(bType);
      })
      .filter(ct => {
        const noTags = filters.tag.length === 0;
        const hits = intersection(
          ct.tags,
          filters.tag.map(t => t.item._id)
        ).length;

        return noTags || (typeaheadModes.tag === 'any' ? hits > 0 : hits === filters.tag.length);
      })
      .map(ct => ({
        ...ct,
        tags: ct.tags.map(t => tagsDict[t]?.name).filter(notUndefined),
      }));
  }, [contentTemplates, filters.tag, typeaheadModes.tag]);

  const fuse = useMemo(() => {
    return new Fuse(processedContents, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name', 'content'],
    });
  }, [processedContents]);

  const fuseSearchResults = useMemo(() => {
    if (!searchValue) return undefined;
    return fuse.search(searchValue).map(s => s.item);
  }, [fuse, searchValue]);

  const tagOpts = property.contentTags.map(t => ({
    key: t._id,
    item: t,
  }));

  return (
    <div className="content-blurbs">
      <div className="px-4 mt-4">
        {canManageLibraries(user) && (
          <BubbleButton
            className="mb-3"
            variant={variant}
            block
            onClick={onAddNewContentClick}
            icon="sparkles"
          >
            Create Content
          </BubbleButton>
        )}
        <div className="-mb-6 text-right text-xs relative">
          <BubbleButton
            variant="white"
            inverse
            iconSuffix="filter"
            className="p-1-i"
            iconClasses="text-smoke-lightest"
            onClick={() =>
              setTypeaheadModes({
                ...typeaheadModes,
                tag: typeaheadModes.tag === 'any' ? 'all' : 'any',
              })
            }
          >
            <span className="text-smoke-lightest">
              {typeaheadModes.tag === 'any' ? 'Any' : 'All'}
            </span>
          </BubbleButton>
        </div>
        <BubbleFormGroup label="Tags" labelClassName="text-white-i">
          <BubbleTypeahead
            block
            variant={variant}
            placeholder="Filter tags shown..."
            menuClassName="max-h-80 overflow-y-auto"
            labelExtractor={opt => opt.name}
            value={filters.tag}
            options={tagOpts}
            onAdd={t => setFilters({ ...filters, tag: [...filters.tag, t] })}
            onRemove={t =>
              setFilters({ ...filters, tag: filters.tag.filter(t2 => t.key !== t2.key) })
            }
            onClearAll={() => setFilters({ ...filters, tag: [] })}
          />
        </BubbleFormGroup>
      </div>
      <BubbleInput
        block
        type="text"
        variant={variant}
        className={`p-4 sticky top-0 bg-${variant}-darkest`}
        value={searchValue}
        placeholder="Search..."
        onValue={setSearchValue}
      />
      <div className="grid grid-cols-1 p-4 pt-0 gap-1">
        {(fuseSearchResults || processedContents).map(ct => (
          <DraggableContentItem
            key={ct._id}
            contentTemplate={convertContentTemplateToLegacy(ct, property)}
            borderColor="ocean"
            onDoubleClick={onDoubleClick ? () => onDoubleClick(ct) : undefined}
            tags={ct.tags}
          />
        ))}
      </div>
    </div>
  );
};

interface ContentBlurbSettingsProps {
  contentTemplate: BareContentTemplateLegacy;
  feedArea: FeedArea;
  onContentBlurbUpdated: (
    feedContent: FeedArea,
    templateId: string,
    replaceDict: ReplacedDict,
    translDict: TranslatedReplacedDict
  ) => void;
}
export function ContentBlurbSettings(props: ContentBlurbSettingsProps) {
  const { languages, defaultLanguage } = useServerInfo();

  const mergedVars = captureBetweenDollars(props.contentTemplate.content).map(m => m[1]);

  const [selectedLang, setSelectedLang] = useState(defaultLanguage.key);
  const [replaceDict, setReplaceDict] = useState(
    generateReplaceDict(mergedVars, props.feedArea.content as any)
  );
  const [showDefault, setShowDefault] = useState(false);
  const [translDict, setTranslDict] = useState(
    generateTranslDict(
      mergedVars,
      languages.map(l => l.key),
      props.feedArea.content as any
    )
  );

  return (
    <div>
      <div className="flex items-stretch bg-eggshell">
        <div className="sticky" style={{ top: 54 }}>
          <div className="grid grid-cols-1">
            <BubbleButton
              variant="navy"
              className="rounded-0-i px-4-i"
              inverse={selectedLang != defaultLanguage.key}
              onClick={() => setSelectedLang(defaultLanguage.key)}
            >
              {defaultLanguage.englishLabel}
            </BubbleButton>
            {languages.map(l => (
              <BubbleButton
                key={l.key}
                className="rounded-0-i px-5-i"
                onClick={() => setSelectedLang(l.key)}
                variant="navy"
                inverse={selectedLang != l.key}
              >
                {l.englishLabel}
              </BubbleButton>
            ))}
          </div>
        </div>
        <div className="flex-grow grid gap-4 grid-cols-1 p-4 bg-white border-l">
          {mergedVars.map(mv => (
            <div key={mv}>
              {selectedLang === defaultLanguage.key ? (
                <BubbleFormGroup label={mv}>
                  <div className="flex items-start">
                    <BubbleInput
                      type="checkbox"
                      className="mr-2"
                      inputClassName="bg-white"
                      checked={replaceDict[mv].enabled}
                      onValue={checked =>
                        setReplaceDict({
                          ...replaceDict,
                          [mv]: { ...replaceDict[mv], enabled: checked },
                        })
                      }
                    />
                    <BubbleTextArea
                      block
                      className="flex-grow"
                      onClick={() =>
                        setReplaceDict({
                          ...replaceDict,
                          [mv]: { ...replaceDict[mv], enabled: true },
                        })
                      }
                      value={replaceDict[mv].value}
                      onValue={val =>
                        setReplaceDict({
                          ...replaceDict,
                          [mv]: { ...replaceDict[mv], value: val },
                        })
                      }
                    />
                  </div>
                </BubbleFormGroup>
              ) : (
                <BubbleFormGroup label={mv} asterisk>
                  {showDefault && (
                    <pre className="bg-eggshell px-2 py-1 rounded mb-3 border shadow">
                      {replaceDict[mv].value}
                    </pre>
                  )}
                  <div className="flex items-start">
                    <BubbleInput
                      type="checkbox"
                      className="mr-2"
                      inputClassName="bg-white"
                      checked={translDict[selectedLang][mv].enabled}
                      onValue={checked =>
                        setTranslDict({
                          ...translDict,
                          [selectedLang]: {
                            ...translDict[selectedLang],
                            [mv]: { ...translDict[selectedLang][mv], enabled: checked },
                          },
                        })
                      }
                    />
                    <BubbleTextArea
                      block
                      className="flex-grow"
                      value={translDict[selectedLang][mv].value}
                      onClick={() =>
                        setTranslDict({
                          ...translDict,
                          [selectedLang]: {
                            ...translDict[selectedLang],
                            [mv]: { ...translDict[selectedLang][mv], enabled: true },
                          },
                        })
                      }
                      onValue={val =>
                        setTranslDict({
                          ...translDict,
                          [selectedLang]: {
                            ...translDict[selectedLang],
                            [mv]: { ...translDict[selectedLang][mv], value: val },
                          },
                        })
                      }
                    />
                  </div>
                </BubbleFormGroup>
              )}
            </div>
          ))}
          {mergedVars.length < 1 && (
            <BubbleAlert variant="ocean">No merged variables found</BubbleAlert>
          )}
        </div>
      </div>
      <div className="p-4 sticky bottom-0 bg-white border-t flex items-center">
        <BubbleButton
          variant="navy"
          className="mr-auto"
          onClick={() =>
            props.onContentBlurbUpdated(
              props.feedArea,
              props.contentTemplate._id,
              replaceDict,
              translDict
            )
          }
          icon="rotate"
        >
          Sync Changes to Feed
        </BubbleButton>
        {selectedLang !== defaultLanguage.key && (
          <div className="ml-3">
            <div className="italic text-xs text-slate">
              *Defaults to {defaultLanguage.englishLabel} if not enabled
            </div>
            <div className="relative">
              <BubbleInput
                type="checkbox"
                className="mr-1"
                inputClassName="bg-white stretched-link"
                checked={showDefault}
                onValue={checked => setShowDefault(checked)}
              />
              Show Defaults
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
